import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Button from "../Button/Button";
import { Down, Left, Right, Up } from "../Icons";
import { useTranslations } from "../../contexts/Translation";
import { scrollToPosition } from "../../lib/scrollToPosition";
import styles from "./HorizontalNavigation.module.css";

type HorizontalNavigationProps = {
  pages: ReactNode[];
  labels?: string[];
  start?: number;
  queryParamKey?: string;
  tabKeys?: string[];
  /** Always render the first label as a clickable link even when the nav collapses to a numeric counter (>10 pages). */
  alwaysShowFirstLabel?: boolean;
};

const HorizontalNavigation = ({ pages, labels, start = 0, queryParamKey, tabKeys, alwaysShowFirstLabel }: HorizontalNavigationProps) => {
  const t = useTranslations("surveyForm");
  const [searchParams, setSearchParams] = useSearchParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const hasMountedRef = useRef(false);
  const previousIndexRef = useRef<number | null>(null);

  const resolvedTabKeys = useMemo(
    () => tabKeys?.length === pages.length ? tabKeys : pages.map((_, index) => `${index}`),
    [pages, tabKeys]
  );

  const getIndexFromHash = useCallback(() => {
    const hash = window.location.hash.replace("#", "");
    const indexFromHash = parseInt(hash, 10);

    if (!isNaN(indexFromHash) && indexFromHash >= 0 && indexFromHash < pages.length) {
      return indexFromHash;
    }

    return start;
  }, [pages.length, start]);

  const getIndexFromQuery = useCallback(() => {
    if (queryParamKey) {
      const currentTab = searchParams.get(queryParamKey);
      const indexFromParam = resolvedTabKeys.indexOf(currentTab ?? "");
      if (indexFromParam >= 0) {
        return indexFromParam;
      }
      return start;
    }

    return start;
  }, [queryParamKey, resolvedTabKeys, searchParams, start]);

  const [hashIndex, setHashIndex] = useState(getIndexFromHash);
  const currentIndex = queryParamKey ? getIndexFromQuery() : hashIndex;

  useEffect(() => {
    if (!queryParamKey) {
      return;
    }

    const nextTab = resolvedTabKeys[currentIndex] ?? resolvedTabKeys[start];
    if (searchParams.get(queryParamKey) === nextTab) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(queryParamKey, nextTab);
    setSearchParams(nextParams, { replace: true });
  }, [currentIndex, queryParamKey, resolvedTabKeys, searchParams, setSearchParams, start]);

  useEffect(() => {
    if (queryParamKey) {
      return;
    }

    const onHashChange = () => {
      const newIndex = getIndexFromHash();
      if (newIndex !== hashIndex) {
        setHashIndex(newIndex);
      }
    };

    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [getIndexFromHash, hashIndex, queryParamKey]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      previousIndexRef.current = currentIndex;
      return;
    }

    if (previousIndexRef.current === currentIndex) {
      return;
    }

    previousIndexRef.current = currentIndex;

    const top = (containerRef.current?.getBoundingClientRect().top ?? 0) + window.scrollY - 100;
    scrollToPosition(top);
  }, [currentIndex]);

  const setCurrentIndex = (nextIndex: number) => {
    if (queryParamKey) {
      const nextTab = resolvedTabKeys[nextIndex] ?? resolvedTabKeys[start];
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set(queryParamKey, nextTab);
      setSearchParams(nextParams);
      return;
    }

    setHashIndex(nextIndex);
    window.location.hash = nextIndex === start ? "" : `#${nextIndex}`;
  };

  const goLeft = () => setCurrentIndex(currentIndex === 0 ? pages.length - 1 : currentIndex - 1);
  const goRight = () => setCurrentIndex(currentIndex === pages.length - 1 ? 0 : currentIndex + 1);

  return (
    <div className={styles.container} ref={containerRef}>
      {pages.length > 1 && (
        <div className={styles.controls}>
          <Button variant='secondary' className={styles.arrowButton} onClick={goLeft} ariaLabel={t("previous")}>
            <span className={styles.iconWide}><Left /></span>
            <span className={styles.iconNarrow}><Up /></span>
          </Button>
          {pages.length > 10 ? (
            <div className={styles.collapsedControls}>
              {alwaysShowFirstLabel && labels?.[0] && (
                <button
                  className={[styles.labelButton, currentIndex === 0 && styles.labelButtonActive].filter(Boolean).join(" ")}
                  type='button'
                  onClick={() => setCurrentIndex(0)}
                >
                  {labels[0]}
                </button>
              )}
              <span>{currentIndex + 1} / {pages.length}</span>
            </div>
          ) : (
            <div className={styles.labelList}>
              {pages.map((_, index) => (
                <button
                  key={index}
                  className={[styles.labelButton, index === currentIndex && styles.labelButtonActive].filter(Boolean).join(" ")}
                  type='button'
                  onClick={() => setCurrentIndex(index)}
                >
                  {labels && labels[index] ? labels[index] : index + 1}
                </button>
              ))}
            </div>
          )}
          <Button variant='secondary' className={styles.arrowButton} onClick={goRight} ariaLabel={t("next")}>
            <span className={styles.iconWide}><Right /></span>
            <span className={styles.iconNarrow}><Down /></span>
          </Button>
        </div>
      )}
      {pages[currentIndex] || null}
    </div>
  );
};

export default HorizontalNavigation;
